import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Effort } from '../../../types/Effort';
import { Task } from '../../../types/Task';
import { Project } from '../../../types/Project';
import { Asset } from '../../../types/Asset';
import TaskActions from './TaskActions';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import { effortService } from '../../../services/effortService';
import {
  calculateExpectedTotalValueWithLogic,
  calculateActualTotalValue,
  getEffortCostTotal,
  hasPriceableEfforts,
} from '../utils';
import { formatDollars } from '../../../utils/formatDollars';
import { usePermissions } from '../../../hooks/usePermissions';
// Removed Pagination import since we're using server-side pagination
import '../styles/adaptive.css';
import InfoTooltip from '../../../components/InfoTooltip';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { Button, Spinner, Modal, StatusBadge, Table, SortableTh, Dropdown, ClampText, EMPTY_MARK, EmptyValue, EXPAND_COL_WIDTH, IdLink, IdText, RowExpandToggle, TableScroll } from '@/components/ui';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

// Add new interface for effort summary
interface EffortSummary {
  type: string;
  description: string;
  quantity: number;
  rate: number;
  totalCost: number;
  unitOfMeasure: string;
  date?: string;
  employeeName?: string;
}

// Add new interface for modal props
interface EffortModalProps {
  show: boolean;
  onHide: () => void;
  task: Task;
  /** Lazily fetched. The task object no longer carries effort rows on the list path. */
  efforts: Effort[];
  loadingEfforts: boolean;
  canToggleVehicleFuel: boolean;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
}

const QUANTIFIED_EFFORTS_COLUMNS = {
  type: true,
  date: true,
  employeeName: true,
  description: true,
  accomplishment: false,
  quantity: true,
  unitOfMeasure: true,
  rate: true,
  fuelSurcharge: true,
  totalCost: true,
  comments: true,
  // Explicit so QP effort views always show DRM labour OT when API returns the fields
  otBreakdown: true,
  timecard: true
} as const;

/** Server allowlist – only these columns are sortable (matches server SORT_ALLOWLIST). */
const SERVER_SORTABLE_COLUMNS = new Set([
  'id', 'taskNumber', 'name', 'contractYear', 'status', 'priority', 'identifiedDate',
  'project', 'foremanArea'
]);

/** The kebab plus cell padding (W-M8). The expander has its own leading column now (V2-072). */
const ACTIONS_COL_WIDTH_QP = '3.5rem';

/** Order must match `<thead>` / body `<td>` so expanded-row `colSpan` matches the table grid. */
const QUANTIFIED_TASK_TABLE_COLUMN_KEYS = [
  'taskNumber',
  'name',
  'project',
  'asset',
  'foremanArea',
  'coding',
  'startDate',
  'createdBy',
  'contractYear',
  'plannedQuantity',
  'actualQuantity',
  'unitOfMeasure',
  'costType',
  'plannedRevenue',
  'totalCosts',
  'status',
  'actions',
] as const;

interface TasksTableProps {
  tasks: Task[];
  onTaskAction: (action: string, task: Task) => void;
  onTaskSelect: (taskIds: number[]) => void;
  onBulkAction: (action: string, tasks: Task[]) => void;
  selectedTasks: number[];
  onTaskClick?: (task: Task) => void;
  showBulkActions?: boolean;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  loading?: boolean;
  /** Merge vehicle fuel PATCH into list state (expanded row / modal stay in sync without refetch). */
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
}

// Add new EffortModal component
const EffortModal: React.FC<EffortModalProps> = ({
  show,
  onHide,
  task,
  efforts,
  loadingEfforts,
  canToggleVehicleFuel,
  onVehicleFuelEffortPatched
}) => {
  // NB: this used to hardcode the `ipad` class, which pulled in ~90 unrelated `.ipad *`
  // rules from quantified-tracking/styles/ResponsiveModals.css at every viewport size.
  return (
    <Modal show={show} onHide={onHide} size="lg" autoHeight>
      <Modal.Header closeButton>
        <Modal.Title>Effort Details - Task {task.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingEfforts ? (
          <div className="tw-flex tw-justify-center tw-py-4">
            <Spinner className="tw-text-primary">Loading efforts...</Spinner>
          </div>
        ) : (
          <TaskEffortsTable
            efforts={efforts}
            className="tw-mb-0"
            columns={{ ...QUANTIFIED_EFFORTS_COLUMNS }}
            canToggleVehicleFuel={canToggleVehicleFuel}
            onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

const TasksTable: React.FC<TasksTableProps> = ({ 
  tasks, 
  onTaskAction,
  onTaskSelect,
  onBulkAction,
  selectedTasks,
  onTaskClick,
  showBulkActions = true,
  sortBy = 'id',
  sortOrder = 'desc',
  onSortChange,
  loading = false,
  onVehicleFuelEffortPatched: onVehicleFuelEffortPatchedFromParent
}) => {
  const { hasPermission } = usePermissions();
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  // Removed currentPage state since we're using server-side pagination
  const [isTableLoaded, setIsTableLoaded] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  // The shared <Table> primitive is a plain function component (no forwardRef), so it can't take
  // a ref directly the way the old hand-rolled <table> did. It still renders a single <table> as
  // the wrapper's only child, so the ResizeObservers below just reach in for it instead.
  const getTableEl = useCallback(
    (): HTMLTableElement | null => tableWrapperRef.current?.querySelector('table') ?? null,
    []
  );
  const [selectedTaskForModal, setSelectedTaskForModal] = useState<Task | null>(null);

  // ---------------------------------------------------------------------------------------------
  // Lazily loaded effort rows
  //
  // The list endpoint no longer sends effort arrays - only a derived `effortSummary` per task,
  // which is all the columns need. The full rows are needed in exactly two places, both of which
  // require a deliberate user action first: expanding a desktop row, and opening the tablet
  // effort modal. So they are fetched per task, on demand, and cached here for as long as this
  // page of results is on screen.
  // ---------------------------------------------------------------------------------------------
  const [effortsByTask, setEffortsByTask] = useState<Record<number, Effort[]>>({});
  const [effortsLoadingIds, setEffortsLoadingIds] = useState<number[]>([]);
  // In-flight ids live in a ref as well as state: the ref is read synchronously by the guard below,
  // so two rapid expands of the same row cannot both get past it before state has updated.
  const effortsInFlightRef = useRef<Set<number>>(new Set());

  const loadEffortsFor = useCallback(async (taskId: number, opts?: { force?: boolean }) => {
    if (!opts?.force && effortsInFlightRef.current.has(taskId)) return;
    effortsInFlightRef.current.add(taskId);
    setEffortsLoadingIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
    try {
      const rows = await effortService.getTaskEfforts(taskId);
      setEffortsByTask((prev) => ({ ...prev, [taskId]: Array.isArray(rows) ? rows : [] }));
    } catch (error) {
      console.error('Failed to load efforts for task', taskId, error);
      // Cache the empty result so the row renders "no efforts" rather than spinning forever.
      setEffortsByTask((prev) => ({ ...prev, [taskId]: [] }));
    } finally {
      effortsInFlightRef.current.delete(taskId);
      setEffortsLoadingIds((prev) => prev.filter((id) => id !== taskId));
    }
  }, []);

  // A new page, filter or refresh replaces `tasks`, at which point anything cached here may be
  // stale. Drop it, then re-fetch for whatever is still expanded so an open row does not silently
  // keep showing the previous result.
  useEffect(() => {
    setEffortsByTask({});
    effortsInFlightRef.current.clear();
  }, [tasks]);

  useEffect(() => {
    expandedRows.forEach((taskId) => {
      if (!effortsByTask[taskId]) void loadEffortsFor(taskId);
    });
  }, [expandedRows, effortsByTask, loadEffortsFor]);

  const effortsFor = useCallback(
    (taskId: number): Effort[] => effortsByTask[taskId] ?? [],
    [effortsByTask]
  );
  const isLoadingEffortsFor = useCallback(
    (taskId: number): boolean => effortsLoadingIds.includes(taskId) && !effortsByTask[taskId],
    [effortsLoadingIds, effortsByTask]
  );

  const handleVehicleFuelEffortPatched = useCallback(
    (effort: Effort) => {
      // Patch the lazily-loaded copy in place so the open row/modal updates without a refetch -
      // this is what the effort rows used to do when they lived on the task object.
      if (effort.taskId != null) {
        setEffortsByTask((prev) => {
          const existing = prev[effort.taskId as number];
          if (!existing) return prev;
          return {
            ...prev,
            [effort.taskId as number]: existing.map((e) =>
              e.id === effort.id ? { ...e, ...effort } : e
            ),
          };
        });
      }
      setSelectedTaskForModal((prev) => {
        if (!prev || effort.taskId == null || prev.id !== effort.taskId) return prev;
        return {
          ...prev,
          efforts: (prev.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
        };
      });
      onVehicleFuelEffortPatchedFromParent?.(effort);
    },
    [onVehicleFuelEffortPatchedFromParent]
  );

  const RESIZABLE_COLUMN_IDS = [
    'taskNumber', 'name', 'project', 'asset', 'foremanArea', 'coding', 'startDate',
    'createdBy', 'contractYear', 'plannedQuantity', 'actualQuantity', 'unitOfMeasure',
    'costType', 'totalCosts', 'plannedRevenue', 'status'
  ];
  const defaultWidths: Record<string, string> = {
    taskNumber: '85px',
    name: '160px',
    project: '120px',
    asset: '120px',
    foremanArea: '130px',
    coding: '100px',
    startDate: '100px',
    createdBy: '120px',
    contractYear: '40px',
    plannedQuantity: '50px',
    actualQuantity: '50px',
    unitOfMeasure: '35px',
    costType: '120px',
    totalCosts: '75px',
    plannedRevenue: '85px',
    // Wide enough for the longest badge ("Active - Marked as Complete" wraps nowhere).
    status: '150px',
    actions: ACTIONS_COL_WIDTH_QP
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  // Simplified device detection
  const checkScreenSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Update orientation
    setOrientation(width > height ? 'landscape' : 'portrait');
    
    // Determine screen size and collapse expanded rows if on tablet
    if (width <= 768) {
      setScreenSize('mobile');
    } else if (width <= 1200) {
      setScreenSize('tablet');
      setExpandedRows([]); // Collapse all rows when switching to tablet
    } else {
      setScreenSize('desktop');
    }
  }, []);

  // Reset expanded rows when screen size changes
  useEffect(() => {
    setExpandedRows([]);
  }, [screenSize]);

  useEffect(() => {
    // Initial check
    checkScreenSize();

    // Add event listeners for window resize and orientation change
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('orientationchange', checkScreenSize);

    // Set up a ResizeObserver to watch for table size changes
    const tableEl = getTableEl();
    if (tableEl) {
      const resizeObserver = new ResizeObserver(() => {
        if (!isTableLoaded) {
          setIsTableLoaded(true);
          // Recheck device size after table is loaded
          setTimeout(checkScreenSize, 100);
        }
      });

      resizeObserver.observe(tableEl);

      return () => {
        window.removeEventListener('resize', checkScreenSize);
        window.removeEventListener('orientationchange', checkScreenSize);
        resizeObserver.disconnect();
      };
    }
  }, [checkScreenSize, isTableLoaded, getTableEl]);

  // Note: No longer need to track currentPage since we use server-side pagination

  // V2-073: YYYY-MM-DD from the shared formatter; the grey en dash when there is none.
  const formatDate = (date: Date | string | undefined) => formatPacificDate(date) || EMPTY_MARK;

  // Function to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number = 20) => {
    if (!text) return '-';
    
    // Adjust maxLength based on device and orientation
    let effectiveMaxLength = maxLength;
    
    if (screenSize === 'tablet') {
      effectiveMaxLength = orientation === 'portrait' ? 15 : 25;
    }
    
    return text.length > effectiveMaxLength ? text.substring(0, effectiveMaxLength) + '...' : text;
  };

  const formatEffortDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Unknown';
    try {
      // Log the date string to see what format it's in
      console.log('Formatting date:', dateStr);
      
      // Try to parse the date
      const date = new Date(dateStr);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateStr);
        return 'Unknown';
      }
      
      // Use a consistent date format for both display and grouping
      return formatDate(date);
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Unknown';
    }
  };

  const handleSelectTask = (taskId: number) => {
    const newSelected = new Set(selectedTasks || []);
    if (selectedTasks && selectedTasks.includes(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    onTaskSelect?.(Array.from(newSelected));
  };

  const handleSelectAll = () => {
    if (selectedTasks && selectedTasks.length === tasks.length) {
      onTaskSelect?.([]);
    } else {
      onTaskSelect?.(tasks.map(task => task.id));
    }
  };

  const handleBulkAction = (action: string) => {
    const selectedTaskObjects = tasks.filter(task => selectedTasks.includes(task.id));
    onBulkAction(action, selectedTaskObjects);
    // Dropdown (Headless UI Menu) closes itself on item activation - no open-state to reset here.
  };

  // Previously getStatusBadgeClass/getStatusDisplay: a local status->colour map plus a
  // portrait-mode path that shortened "Active - Marked as Complete" to a bare "Active" badge
  // with a separate green check icon bolted on. That's the QA report - a Marked-as-Complete
  // task read as plain "Active" (or, worse, a completed-looking green tick) instead of stating
  // its real status, and the wording changed again on the phone. StatusBadge renders the same
  // colour and the same "Active - Marked as Complete" label (from the shared statusUtils map) on
  // every breakpoint, so there is exactly one place this ever needs to be right.
  const toggleRowExpansion = (taskId: number) => {
    if (screenSize === 'tablet') {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTaskForModal(task);
        void loadEffortsFor(taskId);
      }
    } else {
      setExpandedRows(prev => {
        const isOpen = prev.includes(taskId);
        // Only fetch on the way open. Collapsing keeps the cached rows, so re-expanding is instant.
        if (!isOpen) void loadEffortsFor(taskId);
        return isOpen ? prev.filter(id => id !== taskId) : [...prev, taskId];
      });
    }
  };

  const calculateEffortTotal = (efforts: any[]) => {
    const total = efforts.reduce((sum, effort) => {
      const isOther = effort.type === 'Other' || effort.type === 'other';
      const cost = isOther ? (effort.quantity ?? 0) : (effort.quantity ?? 0) * (effort.rate ?? 0);
      return sum + cost;
    }, 0);
    return total || ''; // Return empty string if total is 0
  };

  // Server-based sort: render tasks in server order (no client-side sort).
  const sortedTasks = tasks;

  // Header sort click: only for columns in server allowlist.
  const handleHeaderSort = (field: string) => {
    if (!SERVER_SORTABLE_COLUMNS.has(field) || !onSortChange) return;
    const nextOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(field, nextOrder);
  };

  // Sort icon: only show for sortable columns; use props for active state.
  const getSortIcon = (field: string) => {
    if (!SERVER_SORTABLE_COLUMNS.has(field)) return null;
    if (sortBy !== field) return <i className="bi bi-arrow-down-up tw-text-content-muted tw-ml-1"></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-sort-down-alt tw-ml-1"></i>
      : <i className="bi bi-sort-down tw-ml-1"></i>;
  };

  // Helper function to get column visibility
  const getColumnVisibility = (column: string) => {
    // Base visibility rules
    const baseVisibility: Record<string, boolean> = {
      taskNumber: true,
      name: true,
      project: true,
      asset: true,
      foremanArea: true,
      coding: true,
      startDate: true,
      createdBy: true,
      contractYear: true,
      plannedQuantity: true,
      actualQuantity: true,
      unitOfMeasure: true,
      costType: true,
      totalCosts: true,
      plannedRevenue: true,
      status: true,
      actions: true
    };

    // Adjust visibility based on screen size and orientation
    if (screenSize === 'mobile') {
      return {
        ...baseVisibility,
        project: false,
        asset: false,
        foremanArea: false,
        coding: false,
        createdBy: false,
        contractYear: false,
        plannedQuantity: false,
        actualQuantity: false,
        unitOfMeasure: false,
        costType: false,
        totalCosts: false,
        plannedRevenue: false
      }[column];
    }

    // Tablet (either orientation) keeps EVERY column (W-M8, W-iP11): the table scrolls sideways
    // between the pinned Task ID and Actions columns instead of hiding Cost Type, Planned
    // Revenue, Total Costs and the rest. Only the phone layout (< md, a card list) drops them.

    return baseVisibility[column];
  };

  // Helper function to get font size based on screen size
  const getFontSize = () => {
    switch (screenSize) {
      case 'mobile':
        return '0.875rem';
      case 'tablet':
        return orientation === 'portrait' ? '0.875rem' : '1rem';
      default:
        return '1rem';
    }
  };

  const descriptionDisplay = (task: Task) => {
    if (!task.description) return '-';
    
    if (screenSize !== 'desktop') {
      const shortDesc = truncateText(task.description, 15);
      return <span title={task.description}>{shortDesc}</span>;
    }
    
    return (
      <ClampText lines={1} as="div" title={task.description}>
        {task.description}
      </ClampText>
    );
  };

  interface CellProps {
    value?: {
      name?: string;
    };
  }

  interface ActionCellProps {
    row: {
      original: Task & {
        expanded?: boolean;
        efforts?: any[];
      };
    };
  }

  const columns = [
    {
      Header: 'Task ID',
      accessor: 'taskNumber',
    },
    {
      Header: 'Project',
      accessor: 'project',
      Cell: ({ value }: CellProps) => (
        <div
          className="tw-truncate"
          style={{ maxWidth: '150px' }}
          title={value?.name || ''}
        >
          {value?.name || ''}
        </div>
      ),
    },
    {
      Header: 'Year',
      accessor: 'contractYear',
    },
    {
      Header: 'Asset',
      accessor: 'asset',
      Cell: ({ value }: CellProps) => value?.name || '',
    },
    {
      Header: 'Status',
      accessor: 'status',
    },
    {
      Header: 'Planned Qty',
      accessor: 'plannedQuantity',
    },
    {
      Header: 'Unit',
      accessor: 'unitOfMeasure',
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }: ActionCellProps) => (
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          {hasPriceableEfforts(row.original) && (
            <Button
              variant="outline-primary"
              size="sm"
              active={row.original.expanded}
              className="tw-py-0 tw-px-1"
              onClick={() => toggleRowExpansion(row.original.id)}
              title="View Efforts"
              style={{ minWidth: '28px', fontSize: '0.8rem' }}
            >
              <i className={`bi bi-chevron-${row.original.expanded ? 'up' : 'down'}`} style={{ fontSize: '0.75rem' }}></i>
            </Button>
          )}
          <TaskActions
            task={row.original}
            onAction={(action) => onTaskAction(action, row.original)}
          />
        </div>
      ),
    },
  ];

  const renderEffortDetails = (task: Task) => {
    // +1: the leading expand column (V2-072), always present.
    const effortDetailColSpan =
      QUANTIFIED_TASK_TABLE_COLUMN_KEYS.filter((key) => getColumnVisibility(key)).length + 1;
    // Get taskType and costType from first costing
    const costing = task.taskCostings?.[0];
    const plannedRevenue = calculatePlannedRevenue(task);
    return (
      <tr>
        <td colSpan={Math.max(1, effortDetailColSpan)}>
          <div className="tw-p-3 tw-bg-surface-alt tw-border tw-border-line tw-rounded">
            {isLoadingEffortsFor(task.id) ? (
              <div className="tw-flex tw-justify-center tw-py-4">
                <Spinner className="tw-text-primary">Loading efforts...</Spinner>
              </div>
            ) : (
              <TaskEffortsTable
                efforts={effortsFor(task.id)}
                className="tw-mb-0"
                columns={{ ...QUANTIFIED_EFFORTS_COLUMNS }}
                canToggleVehicleFuel={canToggleVehicleFuel}
                onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
              />
            )}
            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-4 tw-items-center">
              <div>
                <strong>Planned Revenue:</strong> {formatDollars(plannedRevenue)}
              </div>
              <div>
                <strong>Task Type:</strong> {costing?.taskType || '-'}
              </div>
              <div>
                <strong>Cost Type:</strong> {costing?.taskType === 'Unit Rate' ? 'Unit Rate' : (costing?.costType || '-')}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Helper function to check if task was created within 24 hours
  const isRecentlyCreated = (createdAt: Date | string | undefined) => {
    if (!createdAt) return false;
    try {
      const now = new Date();
      const createdAtDate = new Date(createdAt);
      if (isNaN(createdAtDate.getTime())) return false;
      const diffInHours = (now.getTime() - createdAtDate.getTime()) / (1000 * 60 * 60);
      return diffInHours <= 24;
    } catch (error) {
      console.error('Error checking recent creation:', error);
      return false;
    }
  };

  // Add new functions for revenue calculations
  const calculatePlannedRevenue = (task: Task): number => {
    return calculateExpectedTotalValueWithLogic(task);
  };

  const calculateActualRevenue = (task: Task): number => {
    const completionAccomplishment = task.completionAccomplishment || 0;
    return calculateActualTotalValue(task, completionAccomplishment);
  };

  // Total costs from efforts. For Other efforts: quantity only (raw cost); labour uses DRM OT
  // when persisted; markups apply only to revenue. getEffortCostTotal reads the rows when the
  // task has them and the server's summary otherwise, which is the case for every row on this list.
  //
  // Note it also normalises the type key, so " Other" and "OTHER" now land in the Other branch;
  // the inline check here only matched the two exact spellings.
  const calculateTotalCosts = (task: Task): number => getEffortCostTotal(task);

  return (
    <div style={{ fontSize: getFontSize(), flex: '1 1 0%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {showBulkActions && selectedTasks && selectedTasks.length > 0 && (
        <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-content-muted">
            {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
          </span>
          {/* Was a hand-rolled <ul>/<button> menu with its own open state - the one bulk-action
              dropdown in the app that didn't look like every other menu. Dropdown gives it the
              same white surface, shadow-lg and hover:bg-surface-hover row highlight. */}
          <Dropdown>
            <Dropdown.Toggle>Bulk Actions</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleBulkAction('cancel')}>Cancel Selected</Dropdown.Item>
              <Dropdown.Item onClick={() => handleBulkAction('set-active')}>Set to Active</Dropdown.Item>
              <Dropdown.Item onClick={() => handleBulkAction('duplicate')}>Clone selected</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}

      {/* The old synced "sticky scrollbar" strip that sat ABOVE the header row is gone (W-iP11):
          the table's own scroll box below scrolls on both axes, so its horizontal scrollbar is
          at the bottom of the table where people look for it. */}
      {/* Mobile (< md): stacked cards. The wide quantified table can't fit a phone, so
          each task becomes a card with the key fields; "View efforts" opens the existing
          efforts modal. The full table returns at md+. */}
      <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
        {sortedTasks.length === 0 ? (
          <div className="tw-py-6 tw-text-center tw-text-content-muted">No tasks</div>
        ) : (
          sortedTasks.map((task) => {
            const hasEfforts = hasPriceableEfforts(task);
            return (
              <div
                key={`m-${task.id}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <IdLink onClick={(e) => { e.preventDefault(); onTaskClick?.(task); }}>
                    {task.taskNumber || task.id}
                  </IdLink>
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <StatusBadge status={task.status} />
                    <TaskActions task={task} onAction={(action) => onTaskAction(action, task)} />
                  </div>
                </div>
                {task.name && (
                  <div className="tw-mt-1 tw-break-words tw-font-medium tw-text-content">{task.name}</div>
                )}
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Project</div>
                    <ClampText lines={1} as="div" className="tw-text-content" title={task.project?.name || '-'}>
                      {task.project?.name || '-'}
                    </ClampText>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Foreman Area</div>
                    <ClampText lines={1} as="div" className="tw-text-content" title={task.foremanArea?.name || '-'}>
                      {task.foremanArea?.name || '-'}
                    </ClampText>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Start</div>
                    <div className="tw-text-content">{formatDate(task.identifiedDate)}</div>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Planned Qty</div>
                    <div className="tw-tabular-nums tw-text-content">
                      {task.quantifiedTasks?.[0]?.identifiedQuantity ?? '-'}{' '}
                      {task.quantifiedTasks?.[0]?.unitOfMeasure ?? ''}
                    </div>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Planned $</div>
                    <div className="tw-tabular-nums tw-text-content">
                      {formatDollars(calculatePlannedRevenue(task))}
                    </div>
                  </div>
                </div>
                {hasEfforts && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="tw-mt-3 tw-w-full"
                    onClick={() => {
                      setSelectedTaskForModal(task);
                      void loadEffortsFor(task.id);
                    }}
                    title="View efforts"
                  >
                    <i className="bi bi-list-ul tw-mr-1" />
                    View efforts
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* The ONE scroll box for the table (both axes): the sticky header and the pinned Task ID /
          Actions columns attach to it (W-M20, W-M8). Fills ListPage.Body. */}
      <TableScroll ref={tableWrapperRef} className="tw-hidden md:tw-block">
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 10
            }}
          >
            <Spinner className="tw-text-primary">Loading...</Spinner>
          </div>
        )}
        {/* Routed through the shared Table primitive (striped + hover, the app-standard header
            weight) instead of a hand-copied class list - was the "inconsistent header weight"
            report. The resizable-column layout, sort state and expansion stay hand-rolled: this
            table's sort/selection/expansion logic is too entangled for a DataTable migration
            (see the migration note in the PR/report). */}
        <Table striped hover stickyHeader style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
          <thead>
            <tr>
              {/* V2-072 / ACT-04: the expand column leads the row, reserved on every row. */}
              <th
                data-col="expand"
                className="wrap-pin-left"
                style={{ width: EXPAND_COL_WIDTH }}
                aria-label="Expand"
              />
              {getColumnVisibility('taskNumber') && (
                <SortableTh
                  data-col="taskNumber"
                  className="wrap-pin-left wrap-pin-edge"
                  style={{ width: getColumnWidth('taskNumber'), '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
                  label="Task ID"
                  sortKey="taskNumber"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="taskNumber" currentWidthPx={getColumnWidthPx('taskNumber')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('name') && (
                <SortableTh
                  data-col="name"
                  style={{ width: getColumnWidth('name') }}
                  label="Task Name"
                  sortKey="name"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="name" currentWidthPx={getColumnWidthPx('name')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('project') && (
                <SortableTh
                  data-col="project"
                  style={{ width: getColumnWidth('project') }}
                  label="Project"
                  sortKey="project"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="project" currentWidthPx={getColumnWidthPx('project')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('asset') && (
                <th
                  data-col="asset"
                  style={{ width: getColumnWidth('asset') }}
                >
                  Asset
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="asset" currentWidthPx={getColumnWidthPx('asset')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('foremanArea') && (
                <SortableTh
                  data-col="foremanArea"
                  style={{ width: getColumnWidth('foremanArea') }}
                  label="Foreman Area"
                  sortKey="foremanArea"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="foremanArea" currentWidthPx={getColumnWidthPx('foremanArea')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('coding') && (
                <th 
                  data-col="coding"
                  style={{ width: getColumnWidth('coding'), ...(SERVER_SORTABLE_COLUMNS.has('priority') ? { cursor: 'pointer' } : {}) }}
                  onClick={() => handleHeaderSort('priority')}
                >
                  <div className="tw-flex tw-items-center">
                    <InfoTooltip
                      content={
                        <div>
                          <div><strong>Coding Formula:</strong></div>
                          <div style={{ marginTop: '4px' }}>
                            If a task belongs to a project that has a job number, the task is coded to the job number. Otherwise, the task is coded to its assigned activity number.
                          </div>
                          {tasks.some((task) => 
                            task.type === 'Custom' && !task.project?.jobNumber
                          ) && (
                            <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#ffc107' }}>
                              <strong>Note:</strong> For Custom tasks without job numbers, "ZZZ-ZZZZ" is used as a temporary coding number.
                            </div>
                          )}
                        </div>
                      }
                      id="coding-tooltip-qp-tracking"
                      placement="top"
                      maxWidth="400px"
                    >
                      <span>
                        Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }}></i>
                      </span>
                    </InfoTooltip>
                    {getSortIcon('priority')}
                  </div>
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="coding" currentWidthPx={getColumnWidthPx('coding')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('startDate') && (
                <SortableTh
                  data-col="startDate"
                  style={{ width: getColumnWidth('startDate') }}
                  label="Planned Start Date"
                  sortKey="identifiedDate"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="startDate" currentWidthPx={getColumnWidthPx('startDate')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('createdBy') && (
                <th data-col="createdBy" style={{ width: getColumnWidth('createdBy') }}>
                  Created By
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="createdBy" currentWidthPx={getColumnWidthPx('createdBy')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('contractYear') && (
                <SortableTh
                  data-col="contractYear"
                  style={{ width: getColumnWidth('contractYear') }}
                  label="Year"
                  sortKey="contractYear"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="contractYear" currentWidthPx={getColumnWidthPx('contractYear')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('plannedQuantity') && (
                <th data-col="plannedQuantity" style={{ width: getColumnWidth('plannedQuantity') }}>
                  Planned Qty
                  {/* Not sortable: planned quantity comes from relation; server cannot order by it. */}
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="plannedQuantity" currentWidthPx={getColumnWidthPx('plannedQuantity')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('actualQuantity') && (
                <th data-col="actualQuantity" style={{ width: getColumnWidth('actualQuantity') }}>
                  Actual Qty
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="actualQuantity" currentWidthPx={getColumnWidthPx('actualQuantity')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('unitOfMeasure') && (
                <th data-col="unitOfMeasure" style={{ width: getColumnWidth('unitOfMeasure') }}>
                  Unit
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="unitOfMeasure" currentWidthPx={getColumnWidthPx('unitOfMeasure')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('costType') && (
                <th data-col="costType" style={{ width: getColumnWidth('costType') }}>
                  Cost Type
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="costType" currentWidthPx={getColumnWidthPx('costType')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('plannedRevenue') && (
                <th data-col="plannedRevenue" style={{ width: getColumnWidth('plannedRevenue') }}>
                  Planned Revenue
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="plannedRevenue" currentWidthPx={getColumnWidthPx('plannedRevenue')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('totalCosts') && (
                <th data-col="totalCosts" style={{ width: getColumnWidth('totalCosts') }}>
                  Total Costs
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="totalCosts" currentWidthPx={getColumnWidthPx('totalCosts')} onResizeStart={startResize} />
                  )}
                </th>
              )}
              {getColumnVisibility('status') && (
                <SortableTh
                  data-col="status"
                  style={{ width: getColumnWidth('status') }}
                  label="Status"
                  sortKey="status"
                  activeKey={sortBy}
                  direction={sortOrder}
                  onSort={handleHeaderSort}
                >
                  {screenSize === 'desktop' && (
                    <ResizeHandle columnId="status" currentWidthPx={getColumnWidthPx('status')} onResizeStart={startResize} />
                  )}
                </SortableTh>
              )}
              {getColumnVisibility('actions') && (
                // Pinned right and sized to its controls (expander + kebab), so it never overlaps
                // Status and stays on screen while the middle columns scroll (W-M8). Not
                // resizable: narrowing it is exactly what made View Efforts overlap Status.
                <th data-col="actions" className="wrap-pin-right tw-text-right" style={{ width: ACTIONS_COL_WIDTH_QP }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => {
              const isExpanded = expandedRows.includes(task.id);
              // Was `task.efforts?.length ?? 0 > 0`, which parses as `?? (0 > 0)` - it yielded the
              // length itself when efforts existed and `false` otherwise, working only by accident
              // of truthiness.
              const hasEfforts = hasPriceableEfforts(task);

              // Get start and end assets if they exist
              const startLocation = task.taskLocations?.find(loc => loc.type === 'START');
              const endLocation = task.taskLocations?.find(loc => loc.type === 'END');
              const startAsset = startLocation?.asset;
              const endAsset = endLocation?.asset;

              // Format asset display
              const assetDisplay = () => {
                if (startAsset && endAsset && startAsset.id !== endAsset.id) {
                  if (screenSize !== 'desktop') {
                    return <span title={`Start: ${startAsset.name} (#${startAsset.assetNumber}), End: ${endAsset.name} (#${endAsset.assetNumber})`}>Multiple</span>;
                  }
                  return (
                    // Two assets: one line each, so the cell still stops at two lines.
                    <div>
                      <ClampText lines={1}>{`Start: ${startAsset.name} (#${startAsset.assetNumber})`}</ClampText>
                      <ClampText lines={1}>{`End: ${endAsset.name} (#${endAsset.assetNumber})`}</ClampText>
                    </div>
                  );
                } else if (startAsset) {
                  return <ClampText>{`${startAsset.name} (#${startAsset.assetNumber})`}</ClampText>;
                } else if (endAsset) {
                  return <ClampText>{`${endAsset.name} (#${endAsset.assetNumber})`}</ClampText>;
                }
                return <EmptyValue />;
              };

              return (
                <React.Fragment key={task.id}>
                  <tr>
                    <td data-col="expand" className="wrap-pin-left" style={{ width: EXPAND_COL_WIDTH }}>
                      {hasPriceableEfforts(task) && (
                        <RowExpandToggle
                          expanded={isExpanded}
                          onToggle={() => toggleRowExpansion(task.id)}
                          label={isExpanded ? 'Hide efforts' : 'Show efforts'}
                        />
                      )}
                    </td>
                    {getColumnVisibility('taskNumber') && (
                      <td
                        data-col="taskNumber"
                        className="wrap-pin-left wrap-pin-edge"
                        style={{ '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
                      >
                        <div className="tw-flex tw-items-center">
                          {isRecentlyCreated(task.createdAt) && (
                            <div
                              className="tw-mr-2"
                              style={{
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: '#0d6efd',
                                flexShrink: 0
                              }} 
                              title="Created within last 24 hours"
                            />
                          )}
                          <IdLink onClick={(e) => { e.preventDefault(); onTaskClick?.(task); }}>
                            {task.taskNumber || task.id}
                          </IdLink>
                        </div>
                      </td>
                    )}
                    {getColumnVisibility('name') && (
                      <td data-col="name">
                        {/* At most two lines, clamped at the cell edge, full text on hover (W-M7). */}
                        {task.name ? (
                          <ClampText style={{ maxWidth: getColumnWidth('name') }}>{task.name}</ClampText>
                        ) : (
                          <EmptyValue />
                        )}
                      </td>
                    )}
                    {getColumnVisibility('project') && (
                      <td data-col="project">
                        {task.project?.name ? (
                          <ClampText style={{ maxWidth: getColumnWidth('project') }}>{task.project.name}</ClampText>
                        ) : (
                          <EmptyValue />
                        )}
                      </td>
                    )}
                    {getColumnVisibility('asset') && (
                      <td data-col="asset">{assetDisplay()}</td>
                    )}
                    {getColumnVisibility('foremanArea') && (
                      <td data-col="foremanArea">
                        {task.foremanArea?.name ? (
                          <ClampText style={{ maxWidth: getColumnWidth('foremanArea') }}>{task.foremanArea.name}</ClampText>
                        ) : (
                          <EmptyValue />
                        )}
                      </td>
                    )}
                    {getColumnVisibility('coding') && (
                      <td data-col="coding" style={{ textAlign: 'center' }}>
                        {(() => {
                          // If task has a project with job number, display job number
                          // Otherwise, display activity number
                          if (task.project?.jobNumber) {
                            // Coding is plain text, not a pill (W-M5).
                            return <IdText>{task.project.jobNumber}</IdText>;
                          } else if (task.activities && task.activities.length > 0) {
                            return <IdText>{task.activities[0].activityNumber}</IdText>;
                          } else {
                            return <EmptyValue />;
                          }
                        })()}
                      </td>
                    )}
                    {getColumnVisibility('startDate') && (
                      <td data-col="startDate">{formatDate(task.identifiedDate)}</td>
                    )}
                    {getColumnVisibility('createdBy') && (
                      <td data-col="createdBy">
                        {task.createdBy ? `${task.createdBy.name} ${task.createdBy.lastName}` : <EmptyValue />}
                      </td>
                    )}
                    {getColumnVisibility('contractYear') && (
                      <td data-col="contractYear">{task.contractYear}</td>
                    )}
                    {getColumnVisibility('plannedQuantity') && (
                      <td data-col="plannedQuantity">{task.quantifiedTasks?.[0]?.identifiedQuantity || ''}</td>
                    )}
                    {getColumnVisibility('actualQuantity') && (
                      <td data-col="actualQuantity">{task.completionAccomplishment || ''}</td>
                    )}
                    {getColumnVisibility('unitOfMeasure') && (
                      <td data-col="unitOfMeasure">{task.quantifiedTasks?.[0]?.unitOfMeasure}</td>
                    )}
                    {getColumnVisibility('costType') && (
                      <td data-col="costType">
                        {task.taskCostings?.[0]?.taskType === 'Unit Rate' ? 'Unit Rate' : (task.taskCostings?.[0]?.costType || '-')}
                      </td>
                    )}
                    {getColumnVisibility('plannedRevenue') && (
                      <td 
                        data-col="plannedRevenue"
                        style={{ fontSize: getFontSize() }}
                      >
                        {formatDollars(calculatePlannedRevenue(task))}
                      </td>
                    )}
                    {getColumnVisibility('totalCosts') && (
                      <td 
                        data-col="totalCosts"
                        style={{ fontSize: getFontSize() }}
                      >
                        {formatDollars(calculateTotalCosts(task))}
                      </td>
                    )}
                    {getColumnVisibility('status') && (
                      // overflow-hidden: the status column is narrow (60px default) and sits right
                      // before the expand chevron - without a clip boundary a wide badge like
                      // "Active - Marked as Complete" bled past the cell into the chevron ("status
                      // badge overflows to arrow"). StatusBadge is already shrink-0/whitespace-nowrap
                      // so it never wraps; clipping it here is what keeps it out of the next column.
                      <td data-col="status" className="tw-overflow-hidden">
                        <StatusBadge status={task.status} />
                      </td>
                    )}
                    {getColumnVisibility('actions') && (
                      <td data-col="actions" className="wrap-pin-right" style={{ width: ACTIONS_COL_WIDTH_QP }}>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                          <TaskActions
                            task={task}
                            onAction={(action) => onTaskAction(action, task)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                  {isExpanded && screenSize !== 'tablet' && renderEffortDetails(task)}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableScroll>

      {/* Add modal for tablet effort details */}
      {selectedTaskForModal && (
        <EffortModal
          show={!!selectedTaskForModal}
          onHide={() => setSelectedTaskForModal(null)}
          task={selectedTaskForModal}
          efforts={effortsFor(selectedTaskForModal.id)}
          loadingEfforts={isLoadingEffortsFor(selectedTaskForModal.id)}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
        />
      )}
    </div>
  );
};

export { TasksTable };
export default TasksTable;